/**
 * DateUtils.js
 * 
 * A utility module for handling date formatting throughout the Gigify application.
 * Ensures consistent date display across all components.
 */

/**
 * Format a date in a human-readable way
 * @param {Date|Timestamp|String} date - Date to format
 * @param {Object} options - Formatting options for toLocaleDateString
 * @returns {String} Formatted date string
 */
export const formatDate = (date, options = {}) => {
    // Default formatting options for British English format
    const defaultOptions = {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    };
    
    // Merge default options with provided options
    const formatOptions = { ...defaultOptions, ...options };
    
    try {
      // Handle Firebase Timestamp
      if (date && typeof date.toDate === 'function') {
        date = date.toDate();
      }
      
      // Handle string dates or timestamp numbers
      if (typeof date === 'string' || typeof date === 'number') {
        date = new Date(date);
      }
      
      // Handle invalid dates
      if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        return 'the specified date';
      }
      
      // Format the date using British English locale
      return date.toLocaleDateString('en-GB', formatOptions);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'the specified date';
    }
  };
  
  /**
   * Format relative time (e.g., "2 hours ago", "Yesterday")
   * @param {Date|Timestamp|String} timestamp - Date to format 
   * @returns {String} Human-readable relative time
   */
  export const formatTimeAgo = (timestamp) => {
    try {
      if (!timestamp) return 'Unknown time';
      
      // Convert Firebase timestamp to JS Date if needed
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const now = new Date();
      const diffInSeconds = Math.floor((now - date) / 1000);
      const diffInMinutes = Math.floor(diffInSeconds / 60);
      const diffInHours = Math.floor(diffInMinutes / 60);
      const diffInDays = Math.floor(diffInHours / 24);
  
      if (diffInSeconds < 60) {
        return 'Just now';
      } else if (diffInMinutes < 60) {
        return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`;
      } else if (diffInHours < 24) {
        return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
      } else if (diffInDays === 1) {
        return 'Yesterday';
      } else if (diffInDays < 7) {
        return `${diffInDays} days ago`;
      } else {
        // Format date for older notifications
        return date.toLocaleDateString('en-GB', { 
          day: 'numeric', 
          month: 'short', 
          year: 'numeric' 
        });
      }
    } catch (error) {
      console.error('Error formatting relative time:', error);
      return 'Unknown time';
    }
  };
  
  /**
   * Format a date in a short format (e.g., "15 Jan 2025")
   * @param {Date|Timestamp|String} date - Date to format
   * @returns {String} Short formatted date
   */
  export const formatShortDate = (date) => {
    return formatDate(date, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      weekday: undefined
    });
  };
  
  /**
   * Format a date with time (e.g., "Monday, 15 January 2025, 14:30")
   * @param {Date|Timestamp|String} date - Date to format
   * @returns {String} Formatted date with time
   */
  export const formatDateWithTime = (date) => {
    try {
      // Handle Firebase Timestamp
      if (date && typeof date.toDate === 'function') {
        date = date.toDate();
      }
      
      // Handle string dates or timestamp numbers
      if (typeof date === 'string' || typeof date === 'number') {
        date = new Date(date);
      }
      
      // Handle invalid dates
      if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        return 'the specified date and time';
      }
      
      // Format the date with time
      const dateStr = date.toLocaleDateString('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
      
      const timeStr = date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit'
      });
      
      return `${dateStr}, ${timeStr}`;
    } catch (error) {
      console.error('Error formatting date with time:', error);
      return 'the specified date and time';
    }
  };
  
  /**
   * Parse Firebase timestamp data from various formats
   * @param {Any} value - Value to parse as a date
   * @returns {Date|null} JavaScript Date object or null if invalid
   */
  export const parseFirebaseDate = (value) => {
    try {
      // Handle undefined or null
      if (!value) return null;
      
      // Handle Firebase Timestamp
      if (value && typeof value.toDate === 'function') {
        return value.toDate();
      }
      
      // Handle date strings (ISO format)
      if (typeof value === 'string') {
        const date = new Date(value);
        return isNaN(date.getTime()) ? null : date;
      }
      
      // Handle seconds timestamp (Firebase sometimes stores as seconds)
      if (typeof value === 'number') {
        // Check if it's seconds (Firebase) or milliseconds (JS Date)
        const date = value > 9999999999 
          ? new Date(value) // milliseconds
          : new Date(value * 1000); // seconds
        return isNaN(date.getTime()) ? null : date;
      }
      
      // Handle timestamp objects with seconds and nanoseconds
      if (value.seconds !== undefined && value.nanoseconds !== undefined) {
        return new Date(value.seconds * 1000 + value.nanoseconds / 1000000);
      }
      
      // Handle JavaScript Date objects
      if (value instanceof Date) {
        return isNaN(value.getTime()) ? null : value;
      }
      
      return null;
    } catch (error) {
      console.error('Error parsing Firebase date:', error);
      return null;
    }
  };
  
  export default {
    formatDate,
    formatTimeAgo,
    formatShortDate,
    formatDateWithTime,
    parseFirebaseDate
  };